import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trophy, Plus, Trash, Flag, ChartLineUp, Lightning, VideoCamera, Link as LinkIcon } from "@phosphor-icons/react";

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [insights, setInsights] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    match_date: new Date().toISOString().slice(0, 10),
    opponent: "",
    result: "W",
    score_for: "",
    score_against: "",
    minutes_played: "",
    self_rating: "",
    notes: "",
    stat_key_1: "", stat_val_1: "",
    stat_key_2: "", stat_val_2: "",
    stat_key_3: "", stat_val_3: "",
  });

  const load = async () => {
    const { data } = await api.get("/matches");
    setMatches(data);
    try {
      const { data: ins } = await api.get("/matches/insights");
      setInsights(ins);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const stats = {};
    for (let i = 1; i <= 3; i++) {
      const k = form[`stat_key_${i}`]?.trim();
      const v = form[`stat_val_${i}`]?.trim();
      if (k && v) stats[k] = isNaN(Number(v)) ? v : Number(v);
    }
    try {
      await api.post("/matches", {
        match_date: form.match_date,
        opponent: form.opponent,
        result: form.result,
        score_for: form.score_for ? Number(form.score_for) : null,
        score_against: form.score_against ? Number(form.score_against) : null,
        minutes_played: form.minutes_played ? Number(form.minutes_played) : null,
        self_rating: form.self_rating ? Number(form.self_rating) : null,
        stats,
        notes: form.notes,
      });
      setOpen(false);
      setForm({ ...form, opponent: "", score_for: "", score_against: "", minutes_played: "", self_rating: "", notes: "", stat_key_1: "", stat_val_1: "", stat_key_2: "", stat_val_2: "", stat_key_3: "", stat_val_3: "" });
      load();
      toast.success("Match logged. AI plans will factor this in.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    await api.delete(`/matches/${id}`);
    load();
  };

  const [clipModal, setClipModal] = useState(null); // match object
  const [clipForm, setClipForm] = useState({ url: "", title: "", moment_note: "" });

  const addClip = async (e) => {
    e.preventDefault();
    if (!clipModal) return;
    try {
      await api.post(`/matches/${clipModal.id}/clip`, clipForm);
      setClipForm({ url: "", title: "", moment_note: "" });
      setClipModal(null);
      load();
      toast.success("Clip attached");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const delClip = async (matchId, clipId) => {
    await api.delete(`/matches/${matchId}/clip/${clipId}`);
    load();
  };

  const wins = matches.filter((m) => m.result === "W").length;
  const losses = matches.filter((m) => m.result === "L").length;
  const draws = matches.filter((m) => m.result === "D").length;

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Match Log</div>
            <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
              <Trophy size={48} weight="duotone" className="text-[#FF3B30]" /> Matches
            </h1>
            <p className="text-[#A3A3A3] mt-2">Log fixtures. Your AI plans learn from real match data.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-match" className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11 px-6">
                <Plus size={18} weight="bold" className="mr-2" /> Log Match
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[#262626] rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading font-black uppercase text-2xl">Log Match</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Date</Label>
                    <Input type="date" required value={form.match_date} onChange={(e) => setForm({ ...form, match_date: e.target.value })}
                      data-testid="match-date" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Result</Label>
                    <Select value={form.result} onValueChange={(v) => setForm({ ...form, result: v })}>
                      <SelectTrigger data-testid="match-result" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="W">Win</SelectItem>
                        <SelectItem value="L">Loss</SelectItem>
                        <SelectItem value="D">Draw</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="font-heading uppercase tracking-widest text-xs">Opponent</Label>
                  <Input required value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })}
                    data-testid="match-opponent" placeholder="e.g. Northside RFC" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Score For</Label>
                    <Input type="number" value={form.score_for} onChange={(e) => setForm({ ...form, score_for: e.target.value })}
                      data-testid="match-score-for" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Against</Label>
                    <Input type="number" value={form.score_against} onChange={(e) => setForm({ ...form, score_against: e.target.value })}
                      data-testid="match-score-against" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Mins Played</Label>
                    <Input type="number" value={form.minutes_played} onChange={(e) => setForm({ ...form, minutes_played: e.target.value })}
                      data-testid="match-minutes" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Self /10</Label>
                    <Input type="number" min={1} max={10} value={form.self_rating} onChange={(e) => setForm({ ...form, self_rating: e.target.value })}
                      data-testid="match-rating" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                </div>
                <div>
                  <Label className="font-heading uppercase tracking-widest text-xs">Personal Stats (up to 3)</Label>
                  <p className="text-xs text-[#A3A3A3] mt-1">e.g. tries=2, tackles=15, metres=140</p>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="grid grid-cols-2 gap-2 mt-2">
                      <Input value={form[`stat_key_${i}`]} onChange={(e) => setForm({ ...form, [`stat_key_${i}`]: e.target.value })}
                        placeholder="metric" data-testid={`match-stat-key-${i}`}
                        className="rounded-none bg-[#0A0A0A] border-[#262626]" />
                      <Input value={form[`stat_val_${i}`]} onChange={(e) => setForm({ ...form, [`stat_val_${i}`]: e.target.value })}
                        placeholder="value" data-testid={`match-stat-val-${i}`}
                        className="rounded-none bg-[#0A0A0A] border-[#262626]" />
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="font-heading uppercase tracking-widest text-xs">Notes</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="What went well, what didn't" data-testid="match-notes"
                    className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                </div>
                <Button type="submit" data-testid="match-submit"
                  className="w-full rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold">
                  Save Match
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-8">
          <SummaryCard label="Played" value={matches.length} />
          <SummaryCard label="Wins" value={wins} color="#34C759" />
          <SummaryCard label="Losses" value={losses} color="#FF3B30" />
          <SummaryCard label="Draws" value={draws} color="#FF9F0A" />
          <SummaryCard label="Win Rate" value={matches.length ? `${Math.round((wins / matches.length) * 100)}%` : "—"} />
          <SummaryCard label="Avg Rating" value={
            matches.filter((m) => m.self_rating).length
              ? (matches.filter((m) => m.self_rating).reduce((s, m) => s + m.self_rating, 0) / matches.filter((m) => m.self_rating).length).toFixed(1)
              : "—"
          } />
        </div>

        {matches.length === 0 ? (
          <div className="border-2 border-dashed border-[#262626] p-16 text-center" data-testid="empty-matches">
            <Flag size={48} className="text-[#A3A3A3] mx-auto mb-4" />
            <div className="font-heading uppercase tracking-widest text-[#A3A3A3]">No matches logged yet</div>
            <div className="text-sm text-[#A3A3A3] mt-2">Log your fixtures — the AI uses them to refine your plans.</div>
          </div>
        ) : (
          <>
            {/* Match Insights */}
            {insights && insights.matches > 0 && (
              <div className="border border-[#FF3B30] bg-[#FF3B30]/5 p-6 mb-6" data-testid="match-insights">
                <div className="flex items-center gap-3 mb-4">
                  <ChartLineUp size={24} weight="duotone" className="text-[#FF3B30]" />
                  <h2 className="font-heading font-bold uppercase text-lg accent-underline">Monthly Insights</h2>
                </div>

                {insights.prep_insight && (
                  <div className="border border-[#262626] bg-[#0A0A0A] p-4 mb-4" data-testid="prep-insight">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightning size={18} weight="fill" className="text-[#FF9F0A]" />
                      <div className="font-heading uppercase font-bold text-sm">The Data Says</div>
                    </div>
                    <div className="text-sm">
                      When you trained <span className="text-[#FF3B30] font-heading font-bold">3+ sessions</span> in the week before a match, your average self-rating was{" "}
                      <span className="text-[#34C759] font-heading font-bold text-lg">{insights.prep_insight.high_prep_avg}/10</span>
                      {" "}({insights.prep_insight.high_prep_count} matches).
                      With fewer sessions, it dropped to{" "}
                      <span className="text-[#FF3B30] font-heading font-bold text-lg">{insights.prep_insight.low_prep_avg}/10</span>
                      {" "}({insights.prep_insight.low_prep_count} matches).
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {insights.best_opponent && (
                    <div className="border border-[#34C759]/40 bg-[#34C759]/5 p-4" data-testid="best-opp">
                      <div className="text-xs uppercase tracking-widest text-[#34C759] mb-1">Best vs</div>
                      <div className="font-heading font-black text-2xl">{insights.best_opponent.opponent}</div>
                      <div className="text-sm text-[#A3A3A3]">{insights.best_opponent.avg_rating}/10 · {insights.best_opponent.count} game{insights.best_opponent.count > 1 ? "s" : ""}</div>
                    </div>
                  )}
                  {insights.worst_opponent && (
                    <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4" data-testid="worst-opp">
                      <div className="text-xs uppercase tracking-widest text-[#FF3B30] mb-1">Toughest vs</div>
                      <div className="font-heading font-black text-2xl">{insights.worst_opponent.opponent}</div>
                      <div className="text-sm text-[#A3A3A3]">{insights.worst_opponent.avg_rating}/10 · {insights.worst_opponent.count} game{insights.worst_opponent.count > 1 ? "s" : ""}</div>
                    </div>
                  )}
                </div>

                {insights.monthly && insights.monthly.length > 0 && (
                  <div className="border border-[#262626] bg-[#0A0A0A]">
                    <div className="grid grid-cols-6 gap-2 p-3 border-b border-[#262626] text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">
                      <div>Month</div>
                      <div className="text-center">Played</div>
                      <div className="text-center">W</div>
                      <div className="text-center">L</div>
                      <div className="text-center">Win %</div>
                      <div className="text-center">Avg</div>
                    </div>
                    {insights.monthly.map((m) => (
                      <div key={m.month} className="grid grid-cols-6 gap-2 p-3 border-b border-[#1C1C1C] last:border-0 text-sm items-center" data-testid={`month-${m.month}`}>
                        <div className="font-heading font-bold uppercase">{m.month}</div>
                        <div className="text-center">{m.played}</div>
                        <div className="text-center text-[#34C759] font-heading font-bold">{m.wins}</div>
                        <div className="text-center text-[#FF3B30] font-heading font-bold">{m.losses}</div>
                        <div className="text-center">{m.win_rate}%</div>
                        <div className="text-center font-heading font-bold">{m.avg_rating ?? "—"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Match List */}
            <div className="grid gap-3">
              {matches.map((m) => (
              <div key={m.id} className="border border-[#262626] bg-[#121212] p-5" data-testid={`match-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 flex items-center justify-center font-heading font-black text-2xl ${
                      m.result === "W" ? "bg-[#34C759] text-white" :
                      m.result === "L" ? "bg-[#FF3B30] text-white" :
                      "bg-[#FF9F0A] text-black"
                    }`}>
                      {m.result}
                    </div>
                    <div>
                      <div className="font-heading font-bold uppercase text-xl">vs {m.opponent}</div>
                      <div className="text-xs text-[#A3A3A3] mt-1">
                        {new Date(m.match_date).toLocaleDateString()}
                        {(m.score_for !== null && m.score_against !== null) && ` · ${m.score_for}-${m.score_against}`}
                        {m.minutes_played && ` · ${m.minutes_played} min`}
                        {m.self_rating && ` · ${m.self_rating}/10`}
                      </div>
                      {Object.keys(m.stats || {}).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {Object.entries(m.stats).map(([k, v]) => (
                            <span key={k} className="border border-[#262626] px-2 py-0.5 text-xs">
                              <span className="text-[#A3A3A3]">{k}:</span> <span className="font-heading font-bold">{v}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      {m.notes && <div className="text-xs text-[#A3A3A3] italic mt-2">{m.notes}</div>}
                      {(m.clips || []).length > 0 && (
                        <div className="mt-3 space-y-2" data-testid={`clips-${m.id}`}>
                          {m.clips.map((c) => (
                            <div key={c.id} className="border border-[#262626] bg-[#0A0A0A] p-2 flex items-center gap-3 text-sm">
                              <VideoCamera size={18} weight="fill" className="text-[#FF3B30] flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <a href={c.url} target="_blank" rel="noreferrer" className="font-heading uppercase tracking-wide text-sm text-white hover:text-[#FF3B30] block truncate">
                                  {c.title || "Watch clip"}
                                </a>
                                {c.moment_note && <div className="text-xs text-[#A3A3A3] italic">{c.moment_note}</div>}
                              </div>
                              <button onClick={() => delClip(m.id, c.id)} className="text-[#A3A3A3] hover:text-[#FF3B30]" data-testid={`del-clip-${c.id}`}>
                                <Trash size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <button onClick={() => setClipModal(m)} data-testid={`add-clip-${m.id}`}
                      className="border border-[#262626] text-[#A3A3A3] hover:text-[#FF3B30] hover:border-[#FF3B30] px-2 py-1 text-xs font-heading uppercase tracking-widest flex items-center gap-1">
                      <LinkIcon size={12} /> Clip
                    </button>
                    <button onClick={() => del(m.id)} data-testid={`match-delete-${m.id}`} className="text-[#A3A3A3] hover:text-[#FF3B30]">
                      <Trash size={20} />
                    </button>
                  </div>
                </div>
              </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={!!clipModal} onOpenChange={(o) => !o && setClipModal(null)}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase text-2xl">Attach Clip to Match</DialogTitle>
          </DialogHeader>
          {clipModal && (
            <div className="text-sm text-[#A3A3A3]">vs {clipModal.opponent} · {new Date(clipModal.match_date).toLocaleDateString()}</div>
          )}
          <form onSubmit={addClip} className="space-y-4">
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Video URL</Label>
              <Input required type="url" value={clipForm.url} onChange={(e) => setClipForm({ ...clipForm, url: e.target.value })}
                data-testid="clip-url" placeholder="https://youtu.be/…" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Title</Label>
              <Input value={clipForm.title} onChange={(e) => setClipForm({ ...clipForm, title: e.target.value })}
                data-testid="clip-title" placeholder="e.g. 70-yard break in 2nd half"
                className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
            </div>
            <div>
              <Label className="font-heading uppercase tracking-widest text-xs">Moment / Coach Note</Label>
              <Input value={clipForm.moment_note} onChange={(e) => setClipForm({ ...clipForm, moment_note: e.target.value })}
                data-testid="clip-note" placeholder="What to focus on"
                className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
            </div>
            <Button type="submit" data-testid="clip-submit"
              className="w-full rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold">
              Attach Clip
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="border border-[#262626] bg-[#121212] p-4" data-testid={`match-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-heading font-black text-3xl" style={color ? { color } : {}}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-[#A3A3A3] mt-1">{label}</div>
    </div>
  );
}
